// import core components
import { Fragment, useEffect } from "react";
import dynamic from "next/dynamic";
import parser from "html-react-parser";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

// import custom component
import Hero from "@/elements/Hero";
import SEO from "@/elements/Shared/SEO";
const ScoreContainer = dynamic(() => import("@/elements/ScoreContainer"));
const WorkTogether = dynamic(() => import("@/elements/WorkTogether"));
const BlockTextImage = dynamic(
  () => import("@/elements/Shared/BlockTextImage")
);
const DegradeEffect = dynamic(
  () => import("@/elements/Shared/DesignComponents/DegradeEffect")
);
const LottieFilePlayer = dynamic(
  () => import("@/elements/Shared/LottieFilePlayer")
);

// import interfaces
import MenuOptions from "@/interfaces/MenuOptions";

// import data
import servicesPage from "@/data/pages/services/services.json";

// import styles
import styles from "./services.module.scss";

type Props = {
  menuOptions: MenuOptions;
};

export default function Services({ menuOptions }: Props) {
  const { seo, hero, services, scores } = servicesPage;

  if (process.browser) {
    ScrollTrigger.getAll().forEach((t) => {
      t.kill();
    });
  }

  useEffect(() => {
    menuOptions.setMenuColor("white");
  }, [menuOptions]);

  return (
    <>
      <SEO seo={seo} />

      <main className={styles["main"]}>
        <Hero
          isSquareDesignVisible={false}
          heroId="hero--degrade"
          dispositionContainer={2}
          data={hero}
          pageName="services"
        />

        <section className={styles["services-container"]}>
          <div className={`container ${styles["services--header"]}`}>
            {services.header.map((service, i) => {
              return (
                <a
                  key={service.title + i}
                  href={`#${service.slug}`}
                  className={styles["service--card-anchor"]}
                >
                  <div className={styles["service--card-header"]}>
                    <LottieFilePlayer
                      className={styles["services-lottiefile"]}
                      lottieFile={service.lottieFile}
                    />
                  </div>
                  <p className={styles["service--name"]}>
                    {parser(service.title)}
                  </p>
                </a>
              );
            })}
          </div>

          <div className={`container ${styles["services--list"]}`}>
            {services.services_list.map((service, i) => {
              let modulo = i % 2;
              return (
                <Fragment key={i + service.title}>
                  <span id={service.slug} className={styles._anchor}></span>
                  <BlockTextImage
                    isAlternativeSide={service.isAlternativeSide}
                    isAlternativeSideOnMobile={
                      service.isAlternativeSideOnMobile
                    }
                    HtagLevel={2}
                    button={service.button}
                    content={service.content}
                    title={service.title}
                    lottieFile={service.lottieFile}
                    dispositionContainer={4}
                    sectionName={`service-${service.slug}`}
                    optionsAnimImages={{
                      type: "popUp",
                    }}
                    margin={modulo === 0 ? "left" : "right"}
                  />
                </Fragment>
              );
            })}
          </div>
        </section>

        <div className={styles["services--scores-infos"]}>
          <DegradeEffect size={917} position={3} />

          <ScoreContainer scores={scores} />
        </div>

        <WorkTogether isDesignVisible={false} />
      </main>
    </>
  );
}
